import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";

// Distribuzione equa: stessa logica del backend (rotazione, differenza massima 1 lead)
export function splitEvenly(n, ids) {
  const out = Object.fromEntries(ids.map((id) => [id, 0]));
  for (let i = 0; i < n; i++) out[ids[i % ids.length]] += 1;
  return out;
}

export default function BulkAssignDialog({ open, onClose, leadIds, onDone }) {
  const [users, setUsers] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChosen([]);
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale" && u.active))).catch(() => {});
  }, [open]);

  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const dist = chosen.length ? splitEvenly(leadIds.length, chosen) : {};

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/assign-bulk", { lead_ids: leadIds, assigned_to_list: chosen });
      toast.success(`${data.assigned} lead assegnati a ${chosen.length} commerciali`);
      onClose(); onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'assegnazione");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Users className="w-5 h-5" /> Assegna {leadIds.length} lead</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Seleziona uno o più commerciali: i lead verranno distribuiti in modo equo.</p>
        <div className="max-h-64 overflow-y-auto space-y-1 border rounded-md p-2">
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm" data-testid={`bulk-user-${u.id}`}>
              <Checkbox checked={chosen.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`bulk-user-check-${u.id}`} />
              <span className="flex-1">{u.name}</span>
              {chosen.includes(u.id) && <span className="text-xs font-semibold tabular-nums text-primary" data-testid={`bulk-dist-${u.id}`}>{dist[u.id]} lead</span>}
            </label>
          ))}
        </div>
        {chosen.length > 0 && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1" data-testid="bulk-assign-summary">
            <div className="font-semibold">Riepilogo prima della conferma</div>
            {chosen.map((id) => (
              <div key={id} className="flex justify-between"><span>{users.find((u) => u.id === id)?.name}</span><span className="tabular-nums font-medium">{dist[id]}</span></div>
            ))}
            <div className="flex justify-between border-t pt-1 font-semibold"><span>Totale</span><span className="tabular-nums">{leadIds.length}</span></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={confirm} disabled={busy || chosen.length === 0 || leadIds.length === 0} data-testid="bulk-assign-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Conferma assegnazione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
